import { For, Show, createEffect, createMemo, createSignal, on, onCleanup } from "solid-js";
import { resolveHealthSource } from "../lib/health";
import { nearestTripPoint } from "../lib/mapPoints";
import type { HealthSample, TripDetails, TripId, TripStats } from "../types";
import type { MapPointHighlight } from "./mapTypes";
import { SEGMENT_START_FLAG, effectiveSegmentFlags } from "../lib/tripSegments";

const finite = (value: number) => Number.isFinite(value);
export const formatDuration = (seconds: number) => {
  const safe = Math.max(0, Math.round(seconds));
  const days = Math.floor(safe / 86400);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (days) return `${days}d ${Math.floor((safe % 86400) / 3600)}h`;
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};
const formatPace = (seconds: number) =>
  finite(seconds) && seconds > 0
    ? `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")} /km`
    : "—";
const average = (values: number[]) =>
  values.length ? values.reduce((total, value) => total + value, 0) / values.length : undefined;
export const hasMeaningfulSamples = (values: ArrayLike<number>, valid = (value: number) => value > 0) => {
  for (let index = 0; index < values.length; index++) {
    const value = Number(values[index]);
    if (finite(value) && valid(value)) return true;
  }
  return false;
};
const distanceMeters = (trip: TripDetails) => {
  const segmentFlags = effectiveSegmentFlags(trip);
  let total = 0;
  for (let index = 1; index < trip.count; index++) {
    if (segmentFlags[index]! & SEGMENT_START_FLAG) continue;
    const previousLatitude = (trip.positions[(index - 1) * 2 + 1]! * Math.PI) / 180;
    const latitude = (trip.positions[index * 2 + 1]! * Math.PI) / 180;
    const latitudeDelta = latitude - previousLatitude;
    const longitudeDelta = ((trip.positions[index * 2]! - trip.positions[(index - 1) * 2]!) * Math.PI) / 180;
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(previousLatitude) * Math.cos(latitude) * Math.sin(longitudeDelta / 2) ** 2;
    total += 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }
  return total;
};

interface ChartPoint {
  value: number;
  time: number;
  segmentStart: boolean;
  pointIndex: number;
}

interface DashboardMetric {
  label: string;
  value: string;
  detail?: string;
  icon: string;
  tone?: "accent" | "health" | "quality";
}

interface DashboardChart {
  title: string;
  unit: string;
  color: string;
  points: ChartPoint[];
}

const statsSeries = (
  values: Float32Array,
  trip: TripDetails,
  transform = (value: number) => value,
): ChartPoint[] => {
  const points: ChartPoint[] = [];
  const segmentFlags = effectiveSegmentFlags(trip);
  const stride = Math.max(1, Math.ceil(values.length / 360));
  let segmentPending = false;
  const count = Math.min(values.length, trip.count);
  for (let index = 0; index < count; index++) {
    segmentPending ||= Boolean(segmentFlags[index]! & SEGMENT_START_FLAG);
    if (index % stride) continue;
    const value = values[index]!;
    if (!finite(value)) continue;
    points.push({
      value: transform(value),
      time: trip.timestamps[Math.min(index, trip.count - 1)] ?? index,
      segmentStart: segmentPending,
      pointIndex: index,
    });
    segmentPending = false;
  }
  return points;
};

const sourceLabel = (source: string) => {
  if (source.toLowerCase().includes("garmin")) return "Garmin Connect";
  return source.split(".").at(-1)?.replaceAll("_", " ") || source;
};

function TrendChart(props: {
  title: string;
  unit: string;
  points: ChartPoint[];
  color: string;
  startTime: number;
  interactionKey: TripId | null;
  onMapHighlightChange: (highlight?: MapPointHighlight) => void;
}) {
  const [active, setActive] = createSignal<number>();
  let chart!: SVGSVGElement;
  const geometry = createMemo(() => {
    if (props.points.length < 2) return undefined;
    const values = props.points.map((point) => point.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const coordinates = props.points.map((point, index) => ({
      x: (index / Math.max(1, props.points.length - 1)) * 600,
      y: 140 - ((point.value - min) / range) * 112,
    }));
    const line = coordinates
      .map(
        (point, index) => `${index && !props.points[index]!.segmentStart ? "L" : "M"}${point.x},${point.y}`,
      )
      .join(" ");
    const areas: string[] = [];
    let start = 0;
    for (let index = 1; index <= coordinates.length; index++) {
      if (index === coordinates.length || props.points[index]!.segmentStart) {
        const segment = coordinates.slice(start, index);
        if (segment.length > 1)
          areas.push(
            `M${segment[0]!.x},150 ${segment.map((point) => `L${point.x},${point.y}`).join(" ")} L${segment.at(-1)!.x},150 Z`,
          );
        start = index;
      }
    }
    return { coordinates, line, area: areas.join(" "), min, max };
  });
  const moveToPointer = (event: PointerEvent) => {
    const bounds = chart.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    setActive(Math.round(ratio * (props.points.length - 1)));
  };
  const moveByKey = (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const current = active();
    setActive(
      current === undefined
        ? direction > 0
          ? 0
          : props.points.length - 1
        : Math.min(props.points.length - 1, Math.max(0, current + direction)),
    );
  };
  const activePoint = createMemo(() => {
    const index = active();
    const shape = geometry();
    return index === undefined || !shape
      ? undefined
      : { sample: props.points[index]!, point: shape.coordinates[index]! };
  });
  createEffect(() => {
    const point = activePoint();
    props.onMapHighlightChange(
      point ? { pointIndex: point.sample.pointIndex, color: props.color } : undefined,
    );
  });
  createEffect(
    on(
      () => [props.title, props.interactionKey] as const,
      () => setActive(undefined),
      { defer: true },
    ),
  );
  onCleanup(() => props.onMapHighlightChange(undefined));
  return (
    <section class="trend-chart" style={{ "--chart-color": props.color }}>
      <div class="trend-chart__header">
        <span>{props.title}</span>
        <Show
          when={activePoint()}
          fallback={
            <strong>
              {Math.round(geometry()?.max ?? 0)} {props.unit}
            </strong>
          }
        >
          {(point) => (
            <strong>
              {Math.round(point().sample.value * 10) / 10} {props.unit} ·{" "}
              {formatDuration(point().sample.time - props.startTime)}
            </strong>
          )}
        </Show>
      </div>
      <Show when={geometry()} fallback={<div class="trend-chart__empty">Not enough data</div>}>
        {(shape) => (
          <svg
            ref={chart}
            viewBox="0 0 600 150"
            preserveAspectRatio="none"
            role="img"
            aria-label={`${props.title} over trip time`}
            tabindex="0"
            onPointerDown={(event) => {
              chart.setPointerCapture(event.pointerId);
              moveToPointer(event);
            }}
            onPointerMove={(event) => {
              if (event.buttons || event.pointerType === "mouse") moveToPointer(event);
            }}
            onPointerLeave={() => setActive(undefined)}
            onPointerUp={(event) => {
              if (event.pointerType !== "mouse") setActive(undefined);
            }}
            onPointerCancel={() => setActive(undefined)}
            onBlur={() => setActive(undefined)}
            onKeyDown={moveByKey}
          >
            <defs>
              <linearGradient id={`fill-${props.title.replaceAll(" ", "-")}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color={props.color} stop-opacity=".35" />
                <stop offset="1" stop-color={props.color} stop-opacity=".02" />
              </linearGradient>
            </defs>
            <path
              class="trend-chart__area"
              d={shape().area}
              fill={`url(#fill-${props.title.replaceAll(" ", "-")})`}
            />
            <path class="trend-chart__line" d={shape().line} />
            <Show when={activePoint()}>
              {(point) => (
                <>
                  <line
                    class="trend-chart__cursor"
                    x1={point().point.x}
                    x2={point().point.x}
                    y1="18"
                    y2="150"
                  />
                  <circle class="trend-chart__dot" cx={point().point.x} cy={point().point.y} r="5" />
                </>
              )}
            </Show>
          </svg>
        )}
      </Show>
    </section>
  );
}

export default function StatsPanel(props: {
  tripId: TripId | null;
  trip: TripDetails;
  stats: TripStats;
  health: HealthSample[];
  loading: boolean;
  healthSource: string;
  onHealthSourceChange: (source: string) => void;
  onMapHighlightChange: (highlight?: MapPointHighlight) => void;
}) {
  const [expanded, setExpanded] = createSignal(false);
  const [activeChart, setActiveChart] = createSignal("");
  let dragStart: number | undefined;
  createEffect(
    on(
      () => props.tripId,
      () => setExpanded(false),
    ),
  );
  const healthSources = createMemo(() => [...new Set(props.health.map((item) => item.source_package))]);
  const selectedSource = createMemo(() => resolveHealthSource(props.health, props.healthSource));
  const selectedHealth = createMemo(() =>
    props.health.filter((sample) => sample.source_package === selectedSource()),
  );
  const healthValues = (metric: string) =>
    selectedHealth()
      .filter((sample) => sample.metric === metric && finite(sample.value))
      .map((sample) => sample.value);
  const totalHealth = (metric: string) => healthValues(metric).reduce((total, value) => total + value, 0);
  const startTime = createMemo(() => props.trip.timestamps[0] ?? 0);
  const dashboard = createMemo(() => {
    const distance = distanceMeters(props.trip);
    const duration = props.trip.count > 1 ? props.trip.timestamps[props.trip.count - 1]! - startTime() : 0;
    const count = Math.min(props.stats.count, props.trip.count);
    const segmentFlags = effectiveSegmentFlags(props.trip);
    const ascent = elevationGainMeters(props.stats.altitudes, props.trip.timestamps, segmentFlags, count);
    let moving = 0;
    const accuracies: number[] = [];
    const signals: number[] = [];
    for (let index = 0; index < count; index++) {
      if (index > 0 && !(segmentFlags[index]! & SEGMENT_START_FLAG) && props.stats.speeds[index]! > 0.5)
        moving += Math.max(0, props.trip.timestamps[index]! - props.trip.timestamps[index - 1]!);
      if (props.stats.accuracies[index]! > 0) accuracies.push(props.stats.accuracies[index]!);
      if (props.stats.cn0[index]! > 0) signals.push(props.stats.cn0[index]!);
    }
    const battery = Array.from(props.stats.batteries).filter((value) => value > 0 && value <= 100);
    const heart = healthValues("heart_rate_bpm").filter((value) => value > 0);
    const healthSteps = totalHealth("steps");
    const telemetrySteps = props.stats.steps.length ? Number(props.stats.steps.at(-1)) : 0;
    const used = props.stats.satellitesUsed.at(-1) ?? 0;
    const visible = props.stats.satellitesVisible.at(-1) ?? 0;
    const hasMovement = hasMeaningfulSamples(props.stats.speeds, (value) => value > 0.5);
    const activity: DashboardMetric[] = [
      ...(hasMovement
        ? [
            {
              label: "Active time",
              value: formatDuration(moving),
              icon: "fa-person-walking",
              tone: "accent" as const,
            },
          ]
        : []),
      {
        label: "Moving pace",
        value: formatPace(moving && distance ? moving / (distance / 1000) : 0),
        icon: "fa-gauge-high",
        tone: "accent" as const,
      },
      {
        label: "Ascent",
        value: ascent ? `${Math.round(ascent)} m` : "—",
        icon: "fa-mountain",
        tone: "accent" as const,
      },
    ].filter((metric) => metric.value !== "—");
    const health: DashboardMetric[] = [
      {
        label: "Steps",
        value: healthSteps > 0 || telemetrySteps > 0 ? (healthSteps || telemetrySteps).toLocaleString() : "—",
        icon: "fa-shoe-prints",
        tone: "health" as const,
      },
      {
        label: "Heart rate",
        value: heart.length ? `${Math.round(average(heart)!)} bpm` : "—",
        detail: heart.length
          ? `${Math.round(Math.min(...heart))}–${Math.round(Math.max(...heart))} bpm`
          : undefined,
        icon: "fa-heart-pulse",
        tone: "health" as const,
      },
      {
        label: "Calories",
        value: totalHealth("active_calories_kcal")
          ? `${Math.round(totalHealth("active_calories_kcal"))} kcal`
          : "—",
        icon: "fa-fire",
        tone: "health" as const,
      },
      {
        label: "Floors",
        value: totalHealth("floors") ? Math.round(totalHealth("floors")).toLocaleString() : "—",
        icon: "fa-layer-group",
        tone: "health" as const,
      },
    ].filter((metric) => metric.value !== "—");
    const quality: DashboardMetric[] = [
      {
        label: "GPS accuracy",
        value: accuracies.length ? `${average(accuracies)!.toFixed(1)} m` : "—",
        icon: "fa-location-crosshairs",
        tone: "quality" as const,
      },
      {
        label: "Satellites",
        value: visible ? `${used} / ${visible}` : "—",
        detail: visible ? "used / visible" : undefined,
        icon: "fa-satellite-dish",
        tone: "quality" as const,
      },
      {
        label: "Battery used",
        value: battery.length > 1 ? `${Math.max(0, battery[0]! - battery.at(-1)!).toFixed(1)}%` : "—",
        icon: "fa-battery-half",
        tone: "quality" as const,
      },
      {
        label: "GPS signal",
        value: signals.length ? `${average(signals)!.toFixed(1)} dB-Hz` : "—",
        icon: "fa-signal",
        tone: "quality" as const,
      },
    ].filter((metric) => metric.value !== "—");
    return {
      distance: `${(distance / 1000).toFixed(1)} km`,
      span: formatDuration(duration),
      activity,
      health,
      quality,
    };
  });
  const charts = createMemo<DashboardChart[]>(() =>
    [
      {
        title: "Elevation",
        unit: "m",
        color: "#2f9e72",
        points: hasMeaningfulSamples(props.stats.altitudes)
          ? statsSeries(props.stats.altitudes, props.trip)
          : [],
      },
      {
        title: "Speed",
        unit: "km/h",
        color: "#3578e5",
        points: hasMeaningfulSamples(props.stats.speeds)
          ? statsSeries(props.stats.speeds, props.trip, (value) => value * 3.6)
          : [],
      },
      {
        title: "Heart rate",
        unit: "bpm",
        color: "#e25568",
        points: selectedHealth()
          .filter((item) => item.metric === "heart_rate_bpm" && item.value > 0)
          .map((item) => ({
            value: item.value,
            time: item.start_time,
            segmentStart: false,
            pointIndex: nearestTripPoint(props.trip.timestamps, props.trip.count, item.start_time),
          }))
          .filter((item): item is ChartPoint => item.pointIndex !== undefined),
      },
      {
        title: "Battery",
        unit: "%",
        color: "#e09f3e",
        points: hasMeaningfulSamples(props.stats.batteries, (value) => value > 0 && value <= 100)
          ? statsSeries(props.stats.batteries, props.trip)
          : [],
      },
    ].filter((chart) => chart.points.length > 1),
  );
  const selectedChart = createMemo(() => {
    const available = charts();
    return available.find((chart) => chart.title === activeChart()) ?? available[0];
  });
  createEffect(() => {
    const selected = selectedChart();
    if (selected && selected.title !== activeChart()) setActiveChart(selected.title);
  });
  const detailedMetrics = createMemo(() => [
    ...dashboard().activity,
    ...dashboard().health,
    ...dashboard().quality,
  ]);
  return (
    <aside class="stats-panel" classList={{ "is-expanded": expanded() }} aria-label="Trip statistics">
      <button
        type="button"
        class="stats-sheet-handle"
        aria-expanded={expanded()}
        aria-label={expanded() ? "Collapse trip statistics" : "Expand trip statistics"}
        onClick={() => setExpanded((value) => !value)}
        onPointerDown={(event) => {
          dragStart = event.clientY;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerUp={(event) => {
          if (dragStart === undefined) return;
          const distance = event.clientY - dragStart;
          if (Math.abs(distance) > 28) setExpanded(distance < 0);
          dragStart = undefined;
        }}
      >
        <i />
        <span>Trip statistics</span>
        <span class="stats-sheet-summary">
          {dashboard().distance} · {dashboard().span}
        </span>
        <i class="fa" classList={{ "fa-chevron-up": !expanded(), "fa-chevron-down": expanded() }} />
      </button>
      <div class="stats-panel__body">
        <Show
          when={!props.loading}
          fallback={
            <div class="stats-skeleton" aria-label="Loading statistics">
              <For each={Array(8).fill(0)}>{() => <i />}</For>
            </div>
          }
        >
          <div class="stats-rail" classList={{ "has-chart": Boolean(selectedChart()) }}>
            <section class="stats-summary" aria-label="Journey summary">
              <div class="stats-summary__title">
                <i class="fa fa-route" />
                <span>Trip statistics</span>
              </div>
              <div class="stats-primary">
                <article>
                  <span>Distance</span>
                  <strong>{dashboard().distance}</strong>
                </article>
                <article>
                  <span>Journey span</span>
                  <strong>{dashboard().span}</strong>
                </article>
              </div>
            </section>
            <Show when={healthSources().length > 1}>
              <label class="health-source">
                <span>Fitness source</span>
                <select
                  value={props.healthSource}
                  onChange={(event) => props.onHealthSourceChange(event.currentTarget.value)}
                >
                  <option value="preferred">Preferred · Garmin first</option>
                  <For each={healthSources()}>
                    {(source) => <option value={source}>{sourceLabel(source)}</option>}
                  </For>
                </select>
              </label>
            </Show>
            <Show when={detailedMetrics().length}>
              <div class="stats-metrics" aria-label="Detailed trip statistics">
                <For each={detailedMetrics()}>
                  {(metric) => (
                    <article class="stat-inline" classList={{ [`is-${metric.tone}`]: Boolean(metric.tone) }}>
                      <i class={`fa ${metric.icon}`} />
                      <div>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                        <Show when={metric.detail}>
                          <small>{metric.detail}</small>
                        </Show>
                      </div>
                    </article>
                  )}
                </For>
              </div>
            </Show>
            <Show when={selectedChart()}>
              {(chart) => (
                <section class="stats-feature">
                  <div class="stats-chart-tabs" role="tablist" aria-label="Trip chart">
                    <For each={charts()}>
                      {(item) => (
                        <button
                          type="button"
                          role="tab"
                          aria-selected={item.title === chart().title}
                          classList={{ "is-active": item.title === chart().title }}
                          onClick={() => setActiveChart(item.title)}
                        >
                          {item.title}
                        </button>
                      )}
                    </For>
                  </div>
                  <TrendChart
                    {...chart()}
                    startTime={startTime()}
                    interactionKey={props.tripId}
                    onMapHighlightChange={props.onMapHighlightChange}
                  />
                </section>
              )}
            </Show>
            <Show when={!detailedMetrics().length && !selectedChart()}>
              <div class="stats-unavailable">
                <i class="fa fa-circle-info" />
                <span>Detailed telemetry unavailable</span>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    </aside>
  );
}

export function elevationGainMeters(
  altitudes: ArrayLike<number>,
  timestamps: ArrayLike<number>,
  flags: ArrayLike<number> = [],
  count = Math.min(altitudes.length, timestamps.length),
): number {
  let gain = 0;
  let reference: number | undefined;
  let previousTime: number | undefined;
  for (let index = 0; index < count; index++) {
    const altitude = altitudes[index]!;
    const time = timestamps[index]!;
    if (flags[index]! & SEGMENT_START_FLAG) {
      reference = undefined;
      previousTime = undefined;
    }
    if (!finite(altitude) || !finite(time)) {
      reference = undefined;
      previousTime = undefined;
      continue;
    }
    if (previousTime !== undefined && time - previousTime > 30) reference = altitude;
    if (reference === undefined) reference = altitude;
    const change = altitude - reference;
    if (change >= 3) {
      gain += change;
      reference = altitude;
    } else if (change <= -3) {
      reference = altitude;
    }
    previousTime = time;
  }
  return gain;
}
